import pMap from 'p-map';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { toast } from '@gitbook/spine-react';

import { apiClient } from '../../state';
import { ImportBySourceModal } from './ImportBySourceModal';
import { type ImportTarget, isOrganizationImportTarget } from './types';
import { type ImportSource, useImportSources } from './useImportSources';

export interface ImportOptions {
    /** Target to import the content into. */
    target: ImportTarget;
    /**
     * Callback when the content to import has been selected and import has started.
     * If none is passed, `onBack` will be called.
     */
    onStartImport?: (space: api.Space) => void;
    /** Callback when the import has finished. */
    onComplete?: (space: api.Space) => void;
    /** Callback when user clicks on the back button to go back to the selection of source. */
    onBack: () => void;
}

/**
 * The hook encapsulates the logic responsible for importing content.
 * Returns selected import source, a list of all possible import sources and the modals to import content.
 */
export function useContentImport(props: ImportOptions): {
    source: api.ImportContentSource | null;
    sources: ImportSource[];
    onSelectSource: (source: api.ImportContentSource | null) => void;
    modals: React.ReactElement | null;
} {
    const { target, onStartImport, onComplete, onBack } = props;

    const [source, setSource] = React.useState<api.ImportContentSource | null>(null);
    const sources = useImportSources();

    const onSelectSource = (source: api.ImportContentSource | null) => {
        if (source) {
            if (isOrganizationImportTarget(target)) {
                analytics.logEvent(
                    analytics.createOrgEvent(target.organization, {
                        type: 'templates_pick_import',
                        source,
                    })
                );
            } else {
                analytics.logEvent(
                    analytics.createSpaceEvent(target.space, { type: 'edit_pick_import', source })
                );
            }
        }

        setSource(source);
    };

    /** Run an import for a url */
    const executeImport = async (url: string, space: api.Space) => {
        if (!source) {
            return;
        }

        const request: api.RequestImportContent = { url, source };

        if (isOrganizationImportTarget(target) || (!target.page && !target.changeRequest)) {
            return apiClient.spaceContent.importContent(space.id, request);
        }

        if (target.page && target.changeRequest) {
            return apiClient.changeRequestContent.importContentInChangeRequestPageById(
                space.id,
                target.changeRequest,
                target.page,
                request
            );
        }

        if (target.page) {
            return apiClient.spaceContent.importContentInPageById(space.id, target.page, request);
        }

        if (target.changeRequest) {
            return apiClient.changeRequestContent.importContentInChangeRequest(
                space.id,
                target.changeRequest,
                request
            );
        }
    };

    /** Run the import for a url (file or website) */
    const [importing, setImporting] = React.useState(false);
    const startImport = async (urls: string[], space: api.Space) => {
        setImporting(true);
        if (!source) {
            return;
        }

        analytics.logEvent(
            analytics.createEditEvent(space, undefined, {
                type: 'edit_import',
                source,
            })
        );

        if (onStartImport) {
            onStartImport(space);
        } else {
            onBack();
        }

        const promise = pMap(
            urls,
            async (url) => {
                const response = await executeImport(url, space);
                return response ? response.data : undefined;
            },
            { concurrency: 2, stopOnError: false } // throttling here as Hive can run into merge conflicts with too many imports at once
        );
        toast.promise(promise, {
            error: 'Error while importing docs',
        });
        const responses = await promise;

        const isIncompleteImport = responses.some(
            (response) => response && response.totalResources !== response.importedResources
        );
        const hasImportedSomeResources = responses.some(
            (response) => response && response.importedResources > 0
        );

        if (isIncompleteImport) {
            // If some resources still got imported, we navigate the user to
            // the resulting space and show the error toast.
            if (hasImportedSomeResources) {
                if (onComplete) {
                    onComplete(space);
                }
            }

            // Otherwise we keep the modal open and just show the error toast.
            toast.error('Incomplete import', null, {
                description:
                    'Some of your docs were not imported due to file limits, try splitting them into several imports.',
            });
        } else {
            // If the import was successful, we close the modal, navigate
            // the user to the resulting space and show the success toast.
            toast.success('Docs imported', {
                description: 'Docs have been successfully imported. Enjoy!',
            });

            if (onComplete) {
                onComplete(space);
            }
        }
        setImporting(false);
    };

    const sourceInfo = sources.find(({ source: sourceKey }) => sourceKey === source);

    return {
        modals: (
            <ImportBySourceModal
                sourceInfo={sourceInfo}
                startImport={startImport}
                onComplete={(space) => {
                    onComplete?.(space);
                }}
                importing={importing}
                target={target}
                setSource={setSource}
                onBack={onBack}
            />
        ),
        source,
        sources,
        onSelectSource,
    };
}
